import React, { useState } from 'react';
import { Box, Typography, Menu, MenuItem, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Avatar, TablePagination } from '@mui/material';
import { AnalyticsOutlined, MoreVert } from '@mui/icons-material';
import { toast } from 'sonner';
import { downloadAssistantPerformanceCSV } from '../download';
import { useNavigate } from 'react-router-dom';

const boxSx = {
  backgroundColor: '#fff',
  padding: '1rem',
  borderRadius: '8px',
  height: '100%',
  border: '1px solid #E5E7EB'
};

const headerSx = {
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  marginBottom: '1rem'
};

const iconSx = {
  fontSize: "2rem",
  color: '#02042D'
};

const titleSx = {
  fontWeight: 600,
  color: '#02042D'
};

const subtitleSx = {
  fontSize: "0.875rem",
  color: "#6A6A6A"
};

const tableContainerSx = {
  borderRadius: "0.5rem",
  border: '1px solid #E5E7EB',
  '& .MuiTableCell-head': {
    fontWeight: 600,
    color: '#02042D',
    backgroundColor: '#F9FAFB'
  },
  '& .MuiTableCell-body': {
    color: '#4B5563'
  }
};

const avatarSx = {
  width: 30,
  height: 30
};

const AssistantPerformance = ({ 
  assistantPerformance, 
  totalCount, 
  page, 
  setPage, 
  filter, 
  assistant,
  assistantList // Receive as prop
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const rowsPerPage = 5;
  const navigate = useNavigate();

  // Client-side pagination
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  
  // Get only the records for the current page
  const displayedPerformance = assistantPerformance.slice(startIndex, endIndex);

  // Find assistant ID by name
  const findAssistantIdByName = (name) => {
    const found = assistantList.find(a => a.Name === name);
    return found?._id || null;
  };

  // Add navigation handler for assistant
  const navigateToAssistant = (event, assistantName) => {
    event.stopPropagation();
    
    // Find the assistant ID by name
    const assistantId = findAssistantIdByName(assistantName);
    
    if (assistantId) {
      navigate(`/assistant/${assistantId}`);
    } else {
      console.error("Could not find assistant ID for name:", assistantName);
      toast.error(`The assistant "${assistantName}" is no longer available. It may have been deleted or renamed.`, {
        duration: 5000, // Show for 5 seconds for better visibility
        className: "error-toast"
      });
    }
  };

  const handleDownloadCSV = () => {
    toast.promise(downloadAssistantPerformanceCSV({ filter, assistant }), {
      loading: "Downloading CSV File",
      success: "CSV File Downloaded Successfully",
      error: "Failed to Download CSV File"
    });
    setAnchorEl(null);
  };

  const handlePageChange = (event, newPage) => {
    event.preventDefault();
    setPage(newPage);
  };
  
  return (
    <Box sx={boxSx}>
      <Box sx={headerSx}>
        <AnalyticsOutlined sx={iconSx} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={titleSx}>Assistant Performance</Typography>
          <Typography sx={subtitleSx}>
            Overview of each assistant's call count, duration, and cost.
          </Typography>
        </Box>
        <MoreVert sx={{ cursor: "pointer", color: '#6A6A6A' }} onClick={(e) => setAnchorEl(e.currentTarget)} />
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: {
              boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.15)',
              borderRadius: '8px'
            }
          }}
        >
          <MenuItem onClick={handleDownloadCSV} sx={{ color: '#4B5563' }}>
            Download CSV
          </MenuItem>
        </Menu>
      </Box>

      <TableContainer sx={tableContainerSx}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Assistant</TableCell>
              <TableCell>Total Calls</TableCell>
              <TableCell>Avg. Duration (s)</TableCell>
              <TableCell>Total Cost</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedPerformance.length > 0 ? (
              displayedPerformance.map((performance, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: 'center', gap: '.5rem' }}>
                      <Avatar
                        alt={performance.assistantName}
                        src={performance.assistantImage}
                        sx={avatarSx}
                      />
                      <Box>
                        <Typography 
                          sx={{ 
                            fontSize: "14px", 
                            fontWeight: "600", 
                            margin: 0,
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline",
                              color: "primary.main"
                            }
                          }}
                          onClick={(e) => navigateToAssistant(e, performance.assistantName)}
                        >
                          {performance.assistantName.length > 20 ? `${performance.assistantName.slice(0, 15)}...` : performance.assistantName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6A6A6A' }}>
                          {performance.Type || 'Assistant'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                 <TableCell>{performance.totalCalls ?? 0}</TableCell>
                <TableCell>{performance.averageDuration ?? 0}</TableCell>
                <TableCell>${(performance.totalCost ?? 0).toFixed(2)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No data found for applied filters.
                </TableCell>
              </TableRow>
            )}
            {displayedPerformance.length > 0 && displayedPerformance.length < rowsPerPage && (
              Array.from({ length: rowsPerPage - displayedPerformance.length }).map((_, index) => (
                <TableRow key={`empty-${index}`} sx={{ height: '57px' }}>
                  <TableCell colSpan={4} sx={{ border: 'none' }}></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
          onRowsPerPageChange={null}
          sx={{
            fontSize: "0.875rem",
            color: '#4B5563',
            "& .MuiTablePagination-selectLabel": { display: "none" },
            "& .MuiTablePagination-select": { display: "none" },
            "& .MuiTablePagination-selectIcon": { display: "none" },
            borderTop: '1px solid #E5E7EB',
          }}
        />
      </TableContainer>
    </Box>
  );
};

export default AssistantPerformance;
